import React from 'react';
import { PolicyPanel } from './policy-panel.jsx';
import * as T from '../time.js';
import { api } from '../api.js';
import { Empty, Lbl, Loading, Modal, PH, Panel, StatusPill } from './components.jsx';
import { LeaveCancelModal, leaveDates } from './leave-common.jsx';
import { PeoplePicker } from './people-picker.jsx';
import { DEPTS, PEOPLE } from './data.jsx';
import { Button, Checkbox, DataTable, Field, Pill } from './ds/fgcp-ds.jsx';

// leave-admin.jsx — 휴가 관리(휴가 관리자): 종류·발생/조정·공휴일(계획 문서 5.2 2-2b), 직원별 잔여·직원 휴가·관리자 취소(2-2c).
// 권한·규칙은 서버가 판단한다(탭은 GET /leave/types의 actions.manage일 때만 보이고, 관리 API는 403·409로 이유를 돌려준다).

const { useState: uS, useEffect: uE, useCallback: uC } = React;
const box = { padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5, boxSizing: 'border-box' };
const UNIT = { day: '하루', half_day: '반일' };
const thisYear = () => T.parts().y;   // 앱 시간대의 올해
const daysText = n => (n > 0 ? '+' : '') + n + '일';
/** 잔여 관계 표시 — 서버 balance_type_id: null 없음, 자기 id 자기 잔여, 다른 id 그 종류의 잔여 */
const balanceText = t => (t.balance_type_id === null ? '관리 안 함' : t.balance_type_id === t.id ? '자기 잔여' : `${t.balance_type_name} 잔여에서`);

/** 종류 만들기·고치기 — 코드는 만들 때만(식별자). 바꾼 값은 새 신청부터 */
function TypeEditor({ type, types, onClose, onSaved, onToast }) {
  const [code, setCode] = uS('');
  const [name, setName] = uS(type?.name ?? '');
  const [description, setDescription] = uS(type?.description ?? '');
  const [unit, setUnit] = uS(type?.unit ?? 'day');
  const [paid, setPaid] = uS(type?.paid ?? true);
  const [approvalRequired, setApprovalRequired] = uS(type?.approval_required ?? true);
  const [active, setActive] = uS(type?.active ?? true);
  const [balance, setBalance] = uS(!type || type.balance_type_id === null ? 'none' : type.balance_type_id === type.id ? 'self' : type.balance_type_id);
  const [busy, setBusy] = uS(false);
  // 다른 종류의 잔여로 고를 수 있는 것 = 자기 잔여를 갖는 종류(사슬 금지 — 서버도 같은 규칙으로 거부한다)
  const pools = types.filter(t => t.balance_type_id === t.id && t.id !== type?.id);
  const save = () => {
    setBusy(true);
    const body = { name, description: description.trim() || null, unit, paid, approvalRequired, active, balance };
    (type ? api.put('/api/v1/leave/types/' + type.id, body) : api.post('/api/v1/leave/types', { ...body, code: code.trim() }))
      .then(() => { onToast(type ? '종류를 고쳤습니다 — 새 신청부터 쓰입니다' : '종류를 만들었습니다'); onSaved(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Modal width={520} title={type ? `휴가 종류 고치기 — ${type.code}` : '새 휴가 종류'} icon="umbrella" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
        <Button variant="primary" size="sm" disabled={busy || !name.trim() || (!type && !code.trim())} onClick={save}>저장</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-type-editor>
        {!type && <Field label="코드 (영문 소문자·숫자·_, 만든 뒤 바꾸지 않음)" value={code} onChange={e => setCode(e.target.value)} placeholder="예: family_event" />}
        <Field label="이름" value={name} onChange={e => setName(e.target.value)} />
        <Field label="설명 (선택)" value={description} onChange={e => setDescription(e.target.value)} />
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Lbl>단위</Lbl>
            <select style={box} value={unit} onChange={e => setUnit(e.target.value)} aria-label="단위">
              {Object.entries(UNIT).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Lbl>잔여</Lbl>
            <select style={box} value={balance} onChange={e => setBalance(e.target.value)} aria-label="잔여">
              <option value="none">관리 안 함</option>
              <option value="self">자기 잔여</option>
              {pools.map(p => <option key={p.id} value={p.id}>{p.name} 잔여에서</option>)}
            </select>
          </label>
          <Checkbox label="유급" checked={paid} onChange={setPaid} />
          <Checkbox label="결재 필요" checked={approvalRequired} onChange={setApprovalRequired} />
          <Checkbox label="사용" description="신청 목록에 보임" checked={active} onChange={setActive} />
        </div>
      </div>
    </Modal>
  );
}

function TypesPanel({ types, onReload, onToast }) {
  const [editing, setEditing] = uS(null);   // 고칠 종류 또는 'new'
  return (
    <Panel scroll>
      <PH title="휴가 종류" sub={types ? types.length + '개 · 바꾼 값은 새 신청부터' : ''}
        right={<Button variant="primary" size="sm" onClick={() => setEditing('new')}>새 종류</Button>} />
      {!types ? <Loading title="불러오는 중" /> : (
        <DataTable columns={[
          { key: 'name', header: '종류', strong: true, render: t => <button className="link" onClick={() => setEditing(t)}>{t.name}</button> },
          { key: 'code', header: '코드', mono: true, width: 96 },
          { key: 'unit', header: '단위', width: 56, render: t => UNIT[t.unit] ?? t.unit },
          { key: 'balance', header: '잔여', render: t => <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{balanceText(t)}{t.used_by.length ? ` · ${t.used_by.join(', ')}도 씀` : ''}</span> },
          { key: 'approval', header: '결재', width: 56, render: t => (t.approval_required ? '필요' : '없음') },
          { key: 'request_count', header: '신청', mono: true, align: 'right', width: 52 },
          { key: 'active', header: '상태', width: 64, render: t => (t.active ? <Pill tone="ok" dot>사용</Pill> : <Pill tone="neutral" dot={false}>중지</Pill>) },
        ]} rows={types} />
      )}
      {editing && <TypeEditor type={editing === 'new' ? null : editing} types={types ?? []} onToast={onToast}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onReload(); }} />}
    </Panel>
  );
}

/** 발생 — 대상 각자의 그 해 발생 일수를 정한 값으로 맞춘다(더하지 않는다 — 두 번 눌러도 같다) */
function GrantPanel({ pools, onToast, onChanged }) {
  const [typeId, setTypeId] = uS('');
  const [year, setYear] = uS(thisYear());
  const [days, setDays] = uS('');   // 일수는 회사 규정 값 — 화면이 기본값을 정하지 않는다
  const [scope, setScope] = uS('all');
  const [departmentId, setDepartmentId] = uS('');
  const [userIds, setUserIds] = uS([]);
  const [reason, setReason] = uS('');
  const [busy, setBusy] = uS(false);
  const pool = typeId || pools[0]?.id || '';
  const target = scope === 'all' ? { kind: 'all' } : scope === 'department' ? { kind: 'department', departmentId } : { kind: 'user', userId: userIds[0] };
  const ready = pool && days !== '' && (scope === 'all' || (scope === 'department' ? departmentId : userIds.length === 1));
  const grant = () => {
    setBusy(true);
    api.post('/api/v1/leave/balances/grant', { typeId: pool, year: Number(year), days: Number(days), target, reason: reason.trim() || null })
      .then(r => { onToast(`발생 ${days}일 — ${r.changed}명 바뀜, ${r.unchanged}명은 이미 같은 값`); onChanged(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Panel>
      <PH title="발생" sub="그 해 발생 일수를 정한 값으로 맞춘다 — 이미 같은 사람은 그대로" />
      <div style={{ padding: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }} data-grant-form>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>잔여 종류</Lbl>
          <select style={box} value={pool} onChange={e => setTypeId(e.target.value)} aria-label="발생 잔여 종류">
            {pools.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>연도</Lbl>
          <input type="number" style={{ ...box, width: 84 }} value={year} onChange={e => setYear(e.target.value)} aria-label="발생 연도" /></label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>일수</Lbl>
          <input type="number" step="0.5" min="0" style={{ ...box, width: 72 }} value={days} onChange={e => setDays(e.target.value)} aria-label="발생 일수" /></label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>대상</Lbl>
          <select style={box} value={scope} onChange={e => setScope(e.target.value)} aria-label="발생 대상">
            <option value="all">전 재직자</option><option value="department">부서(하위 포함)</option><option value="user">한 사람</option>
          </select></label>
        {scope === 'department' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>부서</Lbl>
            <select style={box} value={departmentId} onChange={e => setDepartmentId(e.target.value)} aria-label="발생 부서">
              <option value="">— 고르세요 —</option>
              {DEPTS.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></label>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 160px' }}><Lbl>사유 (선택)</Lbl>
          <input style={box} value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="예: 정기 발생" aria-label="발생 사유" /></label>
        <Button variant="primary" size="sm" disabled={busy || !ready} onClick={grant}>발생</Button>
      </div>
      {scope === 'user' && <div style={{ padding: '0 16px 16px' }} data-grant-person><PeoplePicker label="사람" value={userIds} onChange={setUserIds} max={1} /></div>}
    </Panel>
  );
}

/** 조정 — 한 사람의 가감(±)과 사유, 그 사람의 잔여·장부 */
function AdjustPanel({ pools, onToast, rev, onChanged }) {
  const [userIds, setUserIds] = uS([]);
  const [typeId, setTypeId] = uS('');
  const [year, setYear] = uS(thisYear());
  const [delta, setDelta] = uS('');
  const [reason, setReason] = uS('');
  const [ledger, setLedger] = uS(null);
  const [busy, setBusy] = uS(false);
  const userId = userIds[0];
  const pool = typeId || pools[0]?.id || '';
  uE(() => {
    if (!userId) { setLedger(null); return undefined; }
    let current = true;
    api.get(`/api/v1/leave/balances/ledger?userId=${userId}&year=${Number(year)}`)
      .then(r => { if (current) setLedger(r); })
      .catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [userId, year, rev, onToast]);
  const adjust = () => {
    setBusy(true);
    api.post('/api/v1/leave/balances/adjust', { userId, typeId: pool, year: Number(year), delta: Number(delta), reason: reason.trim() })
      .then(r => { onToast(`조정했습니다 — 남은 ${r.remainingDays}일`); setDelta(''); setReason(''); onChanged(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  const person = PEOPLE.find(p => p.id === userId);
  return (
    <Panel scroll>
      <PH title="조정·장부" sub="가감(±)과 사유 — 장부에 남는다" />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }} data-adjust-form>
        <PeoplePicker label="사람" value={userIds} onChange={setUserIds} max={1} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>잔여 종류</Lbl>
            <select style={box} value={pool} onChange={e => setTypeId(e.target.value)} aria-label="조정 잔여 종류">
              {pools.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>연도</Lbl>
            <input type="number" style={{ ...box, width: 84 }} value={year} onChange={e => setYear(e.target.value)} aria-label="조정 연도" /></label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>가감 일수</Lbl>
            <input type="number" step="0.5" style={{ ...box, width: 80 }} value={delta} onChange={e => setDelta(e.target.value)} placeholder="-1 · 2" aria-label="가감 일수" /></label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 180px' }}><Lbl>사유</Lbl>
            <input style={box} value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="예: 보상 휴가" aria-label="조정 사유" /></label>
          <Button variant="primary" size="sm" disabled={busy || !userId || !pool || !delta || Number(delta) === 0 || !reason.trim()} onClick={adjust}>조정</Button>
        </div>
      </div>
      {person && ledger && (
        <div style={{ padding: '0 16px 16px' }} data-ledger>
          <div style={{ fontSize: 12.5, marginBottom: 8 }}>
            <b>{person.name}</b> {ledger.year}년 — {ledger.balances.map(b => `${b.name} 남은 ${b.remainingDays}일(발생 ${b.grantedDays} · 조정 ${b.adjustedDays} · 사용 ${b.usedDays} · 진행 ${b.pendingDays})`).join(' / ') || '잔여 관리 종류 없음'}
          </div>
          {ledger.entries.length === 0 ? <Empty title="장부 기록이 없습니다" /> : (
            <DataTable columns={[
              { key: 'kind', header: '구분', width: 56, render: e => (e.kind === 'grant' ? '발생' : '조정') },
              { key: 'type_name', header: '종류', width: 72 },
              { key: 'change', header: '변화', mono: true, align: 'right', width: 64, render: e => daysText(e.change_days) },
              { key: 'reason', header: '사유', render: e => <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{e.reason || '—'}</span> },
              { key: 'actor', header: '한 사람', width: 84, render: e => e.actor_name ?? '장부 도입 전' },
              { key: 'at', header: '시각', mono: true, width: 92, render: e => T.ymd(e.created_at) },
            ]} rows={ledger.entries} />
          )}
        </div>
      )}
    </Panel>
  );
}

/** 공휴일 — 자동으로 가져온 날(출처 피드, 지우지 않음)과 관리자가 넣은 날(회사 휴무일 등). 신청 때 정한 일수는 나중에 바꿔도 다시 세지 않는다 */
function HolidaysPanel({ onToast }) {
  const [year, setYear] = uS(thisYear());
  const [list, setList] = uS(null);
  const [sync, setSync] = uS(null);   // 자동 가져오기 상태(휴가 관리자에게만 온다)
  const [date, setDate] = uS('');
  const [name, setName] = uS('');
  const [rev, setRev] = uS(0);
  uE(() => {
    let current = true;
    api.get('/api/v1/leave/holidays?year=' + Number(year)).then(r => { if (current) { setList(r.holidays); setSync(r.sync ?? null); } }).catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [year, rev, onToast]);
  const add = () => api.post('/api/v1/leave/holidays', { date, name: name.trim() })
    .then(() => { onToast('공휴일을 넣었습니다'); setDate(''); setName(''); setRev(r => r + 1); })
    .catch(e => onToast(e.message));
  const remove = h => api.del('/api/v1/leave/holidays/' + h.date)
    .then(() => { onToast('공휴일을 지웠습니다'); setRev(r => r + 1); })
    .catch(e => onToast(e.message));
  return (
    <Panel scroll>
      <PH title="공휴일" sub="근무일 계산에서 뺀다 — 이미 낸 신청의 일수는 그대로. 공휴일은 자동으로 가져오고, 회사 휴무일은 여기서 넣는다"
        right={<input type="number" style={{ ...box, width: 84 }} value={year} onChange={e => setYear(e.target.value)} aria-label="공휴일 연도" />} />
      {sync && <div data-holiday-sync={sync.level} style={{ padding: '10px 16px 0', fontSize: 12, color: sync.level === 'failing' ? 'var(--crit)' : 'var(--ink-3)' }}>
        자동 가져오기: {sync.lastSuccessAt ? `마지막 성공 ${T.dateTime(sync.lastSuccessAt)}` : '아직 성공 기록 없음'}
        {sync.lastError ? ` · 오류: ${sync.lastError}` : ''} · 출처 <span className="mono">{sync.url}</span></div>}
      <div style={{ padding: 16, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }} data-holiday-form>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>날짜</Lbl>
          <input type="date" style={box} value={date} onChange={e => setDate(e.target.value)} aria-label="공휴일 날짜" /></label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px' }}><Lbl>이름</Lbl>
          <input style={box} value={name} maxLength={50} onChange={e => setName(e.target.value)} placeholder="예: 창립기념일" aria-label="공휴일 이름" /></label>
        <Button variant="primary" size="sm" disabled={!date || !name.trim()} onClick={add}>넣기</Button>
      </div>
      {!list ? <Loading title="불러오는 중" /> : list.length === 0 ? <Empty title={`${year}년 공휴일이 없습니다`} sub="회사 규정에 따라 넣으세요" /> : (
        <DataTable columns={[
          { key: 'date', header: '날짜', mono: true, width: 100 },
          { key: 'name', header: '이름', strong: true },
          { key: 'source', header: '출처', width: 70, render: h => <span data-holiday-source={h.source} style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{h.source === 'feed' ? '자동' : '직접'}</span> },
          { key: 'del', header: '', width: 64, render: h => h.source === 'feed' ? null
            : <Button variant="secondary" size="sm" onClick={() => remove(h)} aria-label={`${h.date} 공휴일 지우기`}>지우기</Button> },
        ]} rows={list.map(h => ({ ...h, id: h.date }))} />
      )}
    </Panel>
  );
}

/**
 * 직원별 잔여 표와 한 사람의 휴가(2-2c) — 표의 이름을 누르면 그 사람의 그 해 신청 전부(남의 이력은 서버가 열람 감사로 남긴다)와
 * 관리자 기준 취소 버튼(승인된 휴가 — 사유 필수는 서버가 actions.cancelReasonRequired로 준다).
 */
function StaffPanel({ onToast }) {
  const [year, setYear] = uS(thisYear());
  const [departmentId, setDepartmentId] = uS('');
  const [table, setTable] = uS(null);
  const [person, setPerson] = uS(null);
  const [requests, setRequests] = uS(null);
  const [cancelling, setCancelling] = uS(null);
  const [rev, setRev] = uS(0);
  uE(() => {
    let current = true;
    api.get(`/api/v1/leave/admin/balances?year=${Number(year)}${departmentId ? '&departmentId=' + departmentId : ''}`)
      .then(r => { if (current) setTable(r); }).catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [year, departmentId, rev, onToast]);
  uE(() => {
    if (!person) { setRequests(null); return undefined; }
    let current = true;
    api.get(`/api/v1/leave/admin/requests?userId=${person.userId}&year=${Number(year)}`)
      .then(r => { if (current) setRequests(r.requests); }).catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [person, year, rev, onToast]);
  const cancel = reason => api.post(`/api/v1/leave/requests/${cancelling.id}/cancel`, { reason })
    .then(() => { setCancelling(null); onToast('휴가를 취소했습니다 — 잔여가 돌아왔습니다'); setRev(r => r + 1); })
    .catch(e => onToast(e.message));
  const pools = table?.pools ?? [];
  return (
    <Panel scroll>
      <PH title="직원별 잔여" sub={table ? `${table.year}년 · ${table.rows.length}명` : ''}
        right={<span style={{ display: 'inline-flex', gap: 8 }}>
          <select style={box} value={departmentId} onChange={e => setDepartmentId(e.target.value)} aria-label="잔여 표 부서">
            <option value="">전체</option>
            {DEPTS.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <input type="number" style={{ ...box, width: 84 }} value={year} onChange={e => setYear(e.target.value)} aria-label="잔여 표 연도" />
        </span>} />
      {!table ? <Loading title="불러오는 중" /> : (
        <DataTable columns={[
          { key: 'name', header: '이름', strong: true, render: r => <button className="link" onClick={() => setPerson(r)} data-staff={r.userId}>{r.name}</button> },
          { key: 'departmentName', header: '부서', width: 96 },
          ...pools.map(p => ({ key: p.id, header: p.name, mono: true, align: 'right', width: 112, render: r => {
            const b = r.balances[p.id];
            return b.recorded ? `${b.remaining} / ${b.granted + b.adjusted}${b.pending ? ` (진행 ${b.pending})` : ''}` : '—';
          } })),
        ]} rows={table.rows.map(r => ({ ...r, id: r.userId }))} />
      )}
      {person && (
        <div style={{ padding: 16, borderTop: '1px solid var(--line)' }} data-staff-requests>
          <div style={{ fontSize: 12.5, marginBottom: 8 }}><b>{person.name}</b> {Number(year)}년 휴가</div>
          {!requests ? <Loading title="불러오는 중" /> : requests.length === 0 ? <Empty title="휴가 신청이 없습니다" /> : (
            <DataTable columns={[
              { key: 'type_name', header: '종류', width: 72 },
              { key: 'date', header: '일자', mono: true, width: 96 },
              { key: 'days', header: '일수', mono: true, align: 'right', width: 46 },
              { key: 'status', header: '상태', width: 64, render: r => <StatusPill s={r.status} /> },
              { key: 'note', header: '', render: r => <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                {r.status === 'cancelled' ? `${r.cancelled_by_name ?? ''} 취소${r.cancel_reason ? ' — ' + r.cancel_reason : ''}` : r.reason ?? ''}</span> },
              { key: 'act', header: '', width: 56, render: r => (r.actions.cancel
                ? <Button variant="secondary" size="sm" onClick={() => setCancelling(r)} aria-label={`${leaveDates(r)} 휴가 취소`}>취소</Button> : null) },
            ]} rows={requests.map(r => ({ ...r, date: leaveDates(r), days: Number(r.amount_days) }))} />
          )}
        </div>
      )}
      {cancelling && <LeaveCancelModal req={cancelling} onClose={() => setCancelling(null)} onConfirm={cancel} />}
    </Panel>
  );
}

/** 휴가 관리 탭 — 직원별 잔여·직원 휴가, 종류·발생/조정·공휴일 */
export function LeaveAdmin({ onToast }) {
  const [types, setTypes] = uS(null);
  const [rev, setRev] = uS(0);
  const load = uC(() => api.get('/api/v1/leave/admin/types').then(r => setTypes(r.types)).catch(e => onToast(e.message)), [onToast]);
  uE(() => { void load(); }, [load]);
  const pools = (types ?? []).filter(t => t.balance_type_id === t.id);   // 발생·조정할 수 있는 잔여 = 자기 잔여를 갖는 종류
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <StaffPanel onToast={onToast} />
      <TypesPanel types={types} onReload={load} onToast={onToast} />
      <div className="row2">
        <GrantPanel pools={pools} onToast={onToast} onChanged={() => setRev(r => r + 1)} />
        <HolidaysPanel onToast={onToast} />
      </div>
      <AdjustPanel pools={pools} onToast={onToast} rev={rev} onChanged={() => setRev(r => r + 1)} />
      <PolicyPanel prefix="leave." title="휴가 정책" onToast={onToast} />
    </div>
  );
}
